/** biome-ignore-all lint/a11y/noNoninteractiveElementToInteractiveRole: WAI-ARIA combobox — listbox/option roles on ul/li while focus stays on the input. */
import { Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  buildToolIndex,
  searchTools,
  type ToolSearchResult,
} from "@/lib/search/tool-index";
import { m } from "@/paraglide/messages";
import { CATEGORIES } from "@/tools/categories";

const CAT_NAME = (id: string) =>
  CATEGORIES.find((c) => c.id === id)?.name() ?? id;

function useResults(query: string): ToolSearchResult[] {
  const index = useMemo(() => buildToolIndex(), []);
  return useMemo(() => searchTools(index, query), [index, query]);
}

function go(path: string) {
  // Dynamic tool paths — a real navigation (TanStack <Link> with a runtime
  // string leaves route params stale), matching the header's tool links.
  window.location.assign(path);
}

/** Shared, keyboard-navigable results list (used by dropdown + mobile overlay). */
function ResultsList({
  results,
  query,
  active,
  listId,
  optionId,
}: {
  results: ToolSearchResult[];
  query: string;
  active: number;
  listId: string;
  optionId: (i: number) => string;
}) {
  if (query.trim().length >= 2 && results.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {m.search_no_results()}
      </p>
    );
  }
  // Group results by category, preserving score order across the flat list.
  const order = results
    .map((r) => r.category)
    .filter((c, i, a) => a.indexOf(c) === i);
  let idx = -1;
  return (
    <ul
      id={listId}
      role="listbox"
      aria-label={m.search_aria_label()}
      className="py-1"
    >
      {order.map((cat) => (
        <li key={cat} role="presentation">
          <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {CAT_NAME(cat)}
          </div>
          <ul role="presentation">
            {results
              .filter((r) => r.category === cat)
              .map((r) => {
                idx++;
                const i = idx;
                const isActive = i === active;
                return (
                  <li
                    key={r.tool.id}
                    role="option"
                    id={optionId(i)}
                    aria-selected={isActive}
                  >
                    <a
                      href={r.tool.basePath}
                      onClick={(e) => {
                        e.preventDefault();
                        go(r.tool.basePath);
                      }}
                      className={`flex items-center gap-3 px-3 py-2 text-sm ${
                        isActive
                          ? "bg-primary/10 text-foreground"
                          : "text-muted-foreground hover:bg-muted/60"
                      }`}
                    >
                      <span
                        className="w-6 shrink-0 text-center text-base"
                        aria-hidden
                      >
                        {r.tool.icon.startsWith("/") ? "🔧" : r.tool.icon}
                      </span>
                      <span className="truncate font-medium text-foreground">
                        {r.tool.name()}
                      </span>
                    </a>
                  </li>
                );
              })}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Keyboard handler shared by both surfaces. Returns the onKeyDown handler. */
function useKeyNav(results: ToolSearchResult[], onClose: () => void) {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), []);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      const r = results[active];
      if (r) {
        e.preventDefault();
        go(r.tool.basePath);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };
  return { active, setActive, onKeyDown };
}

function MobileOverlay({ onClose }: { onClose: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const results = useResults(q);
  const listId = useId();
  const { active, onKeyDown } = useKeyNav(results, onClose);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-background">
      <div className="flex items-center gap-2 border-b border-border p-3">
        <div className="flex flex-1 items-center gap-2 rounded-md border border-border bg-card px-2.5 py-2 focus-within:border-primary/60">
          <Search
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={m.search_placeholder()}
            aria-label={m.search_aria_label()}
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls={listId}
            className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={m.search_close()}
          className="rounded-md p-2 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <ResultsList
          results={results}
          query={q}
          active={active}
          listId={listId}
          optionId={(i) => `${listId}-opt-${i}`}
        />
      </div>
    </div>,
    document.body,
  );
}

export function SearchBox() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const results = useResults(q);
  const listId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const { active, onKeyDown } = useKeyNav(results, () => setOpen(false));

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <>
      {/* Desktop inline search */}
      <div ref={boxRef} className="relative hidden md:block">
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card/60 px-2.5 py-1.5 focus-within:border-primary/60">
          <Search
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={m.search_placeholder()}
            aria-label={m.search_aria_label()}
            role="combobox"
            aria-expanded={open && results.length > 0}
            aria-controls={listId}
            aria-activedescendant={
              open && results[active] ? `${listId}-opt-${active}` : undefined
            }
            className="w-40 bg-transparent text-sm outline-none placeholder:text-muted-foreground lg:w-56"
          />
        </div>
        {open && q.trim().length >= 2 && (
          <div className="absolute right-0 z-50 mt-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl">
            <ResultsList
              results={results}
              query={q}
              active={active}
              listId={listId}
              optionId={(i) => `${listId}-opt-${i}`}
            />
          </div>
        )}
      </div>

      {/* Mobile trigger */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label={m.search_open()}
        className="rounded-md p-2 text-muted-foreground transition hover:text-foreground md:hidden"
      >
        <Search className="h-5 w-5" />
      </button>
      {mobileOpen && <MobileOverlay onClose={() => setMobileOpen(false)} />}
    </>
  );
}
